import React, { useEffect, useMemo } from 'react';
import Icon from '../../../Icon2';
import QueryPills from '../../../QueryPills/QueryPills';
import FieldsList from './FilterComponents/FieldsList';
import TextDropdown from './FilterComponents/TextDropdown';
import SpaceFilterDropdown from './FilterComponents/SpaceFilterDropdown';
import AssetTypeFilterDropdown from './FilterComponents/AssetTypeFilterDropdown';
import FileSizeFilterDropdown from './FilterComponents/FileSizeFilterDropdown';
import CreatedAtFilterDropdown from './FilterComponents/CreatedAtFilterDropdown';
import { useQueryBuilder } from './context/QueryBuilderContext';
import NumberDropdown from './FilterComponents/NumberDropdown';
import { ASSET_PRIVACY_KEY, DEFAULT_FILTERS, FILTER_OPERATORS, SPACE_ASSETS_VIEW_TYPE } from '../../utils/constants';
import { IQueryPillsComponentResponsiveness } from '../../../QueryPills/QueryPills';
import { AssetData } from "../../utils/types";
import { isEmpty } from "lodash";
import CreatedByFilterDropdown from './FilterComponents/CreatedByFilterDropdown';
import LastModifiedAtFilterDropdown from './FilterComponents/LastModifiedAtFilterDropdown';
import LastModifiedByFilterDropdown from './FilterComponents/LastModifiedByFilterDropdown';
import ColorFilterDropdown from './FilterComponents/ColorFilterDropdown';
import LocaleFilterDropdown from './FilterComponents/LocaleFilterDropdown';
import DimensionFilterDropdown from './FilterComponents/DimensionFilterDropdown';
import ScanStatusFilterDropdown from './FilterComponents/ScanStatusFilterDropdown';
import PrivacyFilterDropdown from './FilterComponents/PrivacyFilterDropdown';
import { TruncateProps } from '../../../Truncate/Truncate';

const SpaceAssetsModalFilter: React.FC<any> = (props) => {
  const { linkedSpaces, appliedFilterCount, setAppliedFilterCount, ai_suggestions, setAiSuggestions, localesWithFallback, fetchSearchAssetTypes, fetchSpaceUsers, fetchFieldsAMv2, fetchSearchSpaces, assetScanEnabled = false, assetPrivacyEnabled = false, hideSpaceFilter = false, aiEnabled, aiWarning = false, colorAiWarningMessage } = props
  const defaultFilters = DEFAULT_FILTERS.filter(filter => {
    if (filter.type === 'SPACE') {
      return !hideSpaceFilter;
    }
    if (filter.type === 'SCAN_STATUS') {
      return assetScanEnabled;
    }
    if (filter.type === 'PRIVACY') {
      return assetPrivacyEnabled;
    }
    if (filter.type === 'COLOR') {
      return aiEnabled !== false;
    }
    return true;
  }).map(filter => {
    if (filter.type === 'COLOR' && aiWarning) {
      return {
        ...filter,
        leadingIcon: <Icon icon="Warning" version="v2" size="small" className="color-filter-warning-icon" testId="color-filter-warning-icon" />
      };
    }
    return filter;
  });
  const [filters, setFilters] = React.useState<any>(defaultFilters);
  const [dropDownContent, setDropDownContent] = React.useState<(closeDropdown: () => void) => JSX.Element>(
    () => (closeDropdown) => <></>
  );

  const [initalState, setInitialState] = React.useState(true);
  const [localeInitalState, setLocaleInitalState] = React.useState(true);

  const { viewType } = props;
  const [showViewAllFilters, setShowViewAllFilters] = React.useState(false);

  const { queryBuilder, setCondition, removeAllConditions } = useQueryBuilder();
  const defaultFiltersMap: Record<string, (closeDropdown: () => void) => JSX.Element> = {
    SPACE: (closeDropdown) => (
      <SpaceFilterDropdown
        linkedSpaces={linkedSpaces}
        closeDropdown={closeDropdown}
        initalState={initalState}
        setInitialState={setInitialState}
        fetchSearchSpaces={fetchSearchSpaces}
        onApply={handleSpaceFilterApply}
      />
    ),
    ASSET_TYPE: (closeDropdown) => <AssetTypeFilterDropdown closeDropdown={closeDropdown} fetchSearchAssetTypes={fetchSearchAssetTypes} />,
    FILE_SIZE: (closeDropdown) => <FileSizeFilterDropdown closeDropdown={closeDropdown} />,
    CREATED_AT: (closeDropdown) => <CreatedAtFilterDropdown closeDropdown={closeDropdown} />,
    CREATED_BY: (closeDropdown) => <CreatedByFilterDropdown
      linkedSpaces={props.linkedSpaces}
      closeDropdown={closeDropdown}
      fetchSpaceUsers={fetchSpaceUsers}
    />,
    UPDATED_AT: (closeDropdown) => <LastModifiedAtFilterDropdown closeDropdown={closeDropdown} />,
    UPDATED_BY: (closeDropdown) => <LastModifiedByFilterDropdown
      linkedSpaces={props.linkedSpaces}
      closeDropdown={closeDropdown}
      fetchSpaceUsers={fetchSpaceUsers}
    />,
    COLOR: (closeDropdown) => <ColorFilterDropdown
      linkedSpaces={linkedSpaces}
      ai_suggestions={ai_suggestions}
      setAiSuggestions={setAiSuggestions}
      closeDropdown={closeDropdown}
      warning={aiWarning ? colorAiWarningMessage : undefined}
    />,
    LOCALE: (closeDropdown) => <LocaleFilterDropdown closeDropdown={closeDropdown} localesWithFallback={localesWithFallback} setInitialState={setLocaleInitalState} initalState={localeInitalState} />,
    DIMENSIONS: (closeDropdown) => <DimensionFilterDropdown closeDropdown={closeDropdown} />,
    ...(assetScanEnabled && {
      SCAN_STATUS: (closeDropdown) => <ScanStatusFilterDropdown closeDropdown={closeDropdown} />
    }),
    ...(assetPrivacyEnabled && {
      PRIVACY: (closeDropdown) => <PrivacyFilterDropdown closeDropdown={closeDropdown} />
    })
  };

  const customTypeRenderers: Record<string, (pill: any, closeDropdown: () => void) => JSX.Element> = {
    TEXT: (pill, closeDropdown) => <TextDropdown field={pill.id} closeDropdown={closeDropdown} />,
    NUMBER: (pill, closeDropdown) => <NumberDropdown field={pill.id} closeDropdown={closeDropdown} />
    // SELECT: (pill, closeDropdown) => <SelectDropdown field={pill.id} closeDropdown={closeDropdown} />,
    // DATE: (pill, closeDropdown) => <DateDropdown field={pill.id} closeDropdown={closeDropdown} />,
  };

  const getFieldsListComp = (closeDropdown) => {
    return <FieldsList
      filters={filters}
      setFilters={setFilters}
      closeDropdown={closeDropdown}
      defaultFields={defaultFilters}
      linkedSpaces={props.linkedSpaces}
      fetchFieldsAMv2={fetchFieldsAMv2}
    />;
  }

  const renderDropdownContent = (pill: any) => {
    if (pill.field === 'leadingIcon') {
      setDropDownContent(() => (closeDropdown) => (
        getFieldsListComp(closeDropdown)
      ));
      return;
    }

    // Default field types (e.g., Space, Asset Type, etc.)
    const defaultRenderFn = defaultFiltersMap[pill.type];
    if (defaultRenderFn) {
      setDropDownContent(() => (closeDropdown) => defaultRenderFn(closeDropdown));
      return;
    }

    const customRenderFn = customTypeRenderers[pill.type];
    if (customRenderFn) {
      setDropDownContent(() => (closeDropdown) => customRenderFn(pill, closeDropdown));
      return;
    }

    // Fallback
    setDropDownContent(() => () => <>YTD</>);
  };

  const normalizeDimensionKeys = (selectedKeys: Set<string>): Set<string> => {
    const keysArray = Array.from(selectedKeys);
    const hasDimension = keysArray.some(k => k === 'dimensions.width' || k === 'dimensions.height');
    return new Set([...keysArray.filter(k => k !== 'dimensions.width' && k !== 'dimensions.height'), ...(hasDimension ? ['dimensions'] : [])]);
  };

  // update filter count when all fields are removed from seletion 
  // i.e when filters are cleared

  // Compute queryBuilder-based counts
  const queryBuilderStats = useMemo(() => {
    const selectedKeys = new Set<string>();
    const conditionCounts: Record<string, number> = {};
    let total = 0;

    if (queryBuilder?.query?.$and) {
      queryBuilder.query.$and.forEach(condition => {
        const key = Object.keys(condition)[0];
        const value = condition[key];

        if (!((initalState && key === 'space_uid') || (localeInitalState && key === 'locale'))) {
          selectedKeys.add(key);
        }

        if (value?.$in && Array.isArray(value.$in)) {
          const count = value.$in.length;
          if (!((initalState && key === 'space_uid') || (localeInitalState && key === 'locale'))) {
            conditionCounts[key] = count;
            total += count;
          }
        } else if (key !== 'parent_uid' && key !== 'uid') {
          total += 1;
          if (key === ASSET_PRIVACY_KEY) {
            conditionCounts[key] = 1;
          }
        }
      });
    }

    const normalizedKeys = normalizeDimensionKeys(selectedKeys);

    return { selectedKeys: normalizedKeys, conditionCounts, total };
  }, [queryBuilder]);

  // Compute ai_suggestions.color badge count
  const aiSuggestionStats = useMemo(() => {
    const selectedKeys = new Set<string>();
    const conditionCounts: Record<string, number> = {};
    const count = ai_suggestions?.color?.length || 0;

    if (count > 0) {
      selectedKeys.add('color');
      conditionCounts['color'] = count;
    }

    return {
      selectedKeys,
      conditionCounts,
      total: count
    };
  }, [ai_suggestions]);

  // combine both queryBuilderStats and aiSuggestionStats
  useEffect(() => {
    const combinedSelected = new Set([
      ...queryBuilderStats.selectedKeys,
      ...aiSuggestionStats.selectedKeys
    ]);

    const combinedCounts = {
      ...queryBuilderStats.conditionCounts,
      ...aiSuggestionStats.conditionCounts
    };

    const totalFilters = queryBuilderStats.total + aiSuggestionStats.total;

    const updatedFilters = filters.map(filter => {
      const isSelected = combinedSelected.has(filter.id);
      const badgeCount = combinedCounts[filter.id];

      return {
        ...filter,
        isSelected,
        badgeText: badgeCount !== undefined ? badgeCount : undefined
      };
    });

    setFilters(updatedFilters);
    setAppliedFilterCount(totalFilters);
  }, [queryBuilderStats, aiSuggestionStats]);

  useEffect(() => {
    if (!filters || filters.length === 0) {
      setAppliedFilterCount(0);
    }
  }, [filters]);

  // `filters` state is only seeded from `defaultFilters` once, at mount (see useState above),
  // so if `aiEnabled`/`aiWarning`/`colorAiWarningMessage` arrive or change after that (e.g. an
  // async availability read resolving after the initial render), the COLOR filter's presence
  // and leadingIcon would otherwise never update. Re-sync it here whenever those props change.
  useEffect(() => {
    setFilters((prevFilters: any[]) => {
      const hasColorFilter = prevFilters.some(filter => filter.type === 'COLOR');

      if (aiEnabled === false) {
        return prevFilters.filter(filter => filter.type !== 'COLOR');
      }

      if (!hasColorFilter) {
        const colorFilterDefault = DEFAULT_FILTERS.find(filter => filter.type === 'COLOR');
        if (!colorFilterDefault) {
          return prevFilters;
        }
        const colorFilter = aiWarning
          ? {
              ...colorFilterDefault,
              leadingIcon: <Icon icon="Warning" version="v2" size="small" className="color-filter-warning-icon" testId="color-filter-warning-icon" />
            }
          : colorFilterDefault;
        const spaceIndex = prevFilters.findIndex(filter => filter.type === 'SPACE');
        const insertAt = spaceIndex === -1 ? 0 : spaceIndex + 1;
        return [...prevFilters.slice(0, insertAt), colorFilter, ...prevFilters.slice(insertAt)];
      }

      return prevFilters.map(filter => {
        if (filter.type !== 'COLOR') {
          return filter;
        }
        if (aiWarning) {
          return {
            ...filter,
            leadingIcon: <Icon icon="Warning" version="v2" size="small" className="color-filter-warning-icon" testId="color-filter-warning-icon" />
          };
        }
        const { leadingIcon, ...filterWithoutLeadingIcon } = filter;
        return filterWithoutLeadingIcon;
      });
    });
  }, [aiEnabled, aiWarning]);

  // Called when space filter is applied — clears all other user filters in one setQueryBuilder call
  const handleSpaceFilterApply = (selectedSpaceItems: string[]) => {
    const hasSelection = selectedSpaceItems.length > 0;
    const spaceUids = hasSelection ? selectedSpaceItems : linkedSpaces;
    const folderUid = props.selectedFolder ? (props.selectedFolder as AssetData).uid : '';

    // When no specific space is selected (clearing the space filter), do not retain space_uid in $and
    // at root — matches the initial query state where space_uid has shouldRemove:true (dam-ui parity).
    // Inside a folder, always retain space_uid for workspace scoping.
    const fieldsToRetain = [
      'parent_uid',
      'uid',
      'locale',
      ...(hasSelection || props.selectedFolder ? ['space_uid'] : []),
    ];

    const defaultConditions: Record<string, any> = {
      parent_uid: {
        [FILTER_OPERATORS.EQUALS]: folderUid || 'root',
      },
      locale: {
        [FILTER_OPERATORS.IN]: localesWithFallback?.map((locale: any) => locale?.code) || [],
      },
      ...(hasSelection || props.selectedFolder ? {
        space_uid: {
          [FILTER_OPERATORS.IN]: spaceUids,
        },
      } : {}),
    };

    setInitialState(!hasSelection);
    setLocaleInitalState(true);
    removeAllConditions(fieldsToRetain, defaultConditions);

    // dam-ui parity: setAiSuggestions({}) on space change — clear all AI filter content.
    // Keep only space_uids so the color filter knows which spaces to scope suggestions to.
    if (ai_suggestions) {
      setAiSuggestions({ space_uids: spaceUids });
    }
  };

  //clears all filters and resets the initial state
  const clearAllFilters = () => {
    setInitialState(true);
    setLocaleInitalState(true);
    const folderUid = props.selectedFolder ? (props.selectedFolder as AssetData).uid : '';

    // At root: exclude space_uid from retained fields — space scoping at root is via the
    // top-level `spaces` payload field (matches dam-ui). Retaining it here causes a stale
    // space_uid condition in $and that persists into subsequent filter applications.
    // Inside a folder: retain space_uid to preserve the folder's workspace scoping.
    const fieldsToRetain = [
      'parent_uid',
      'uid',
      'locale',
      ...(props.selectedFolder ? ['space_uid'] : []),
    ];

    const defaultConditions: Record<string, any> = {
      parent_uid: {
        [FILTER_OPERATORS.EQUALS]: folderUid || 'root',
      },
      locale: {
        [FILTER_OPERATORS.IN]: localesWithFallback?.map((locale: any) => locale?.code) || [],
      },
      ...(props.selectedFolder ? {
        space_uid: {
          [FILTER_OPERATORS.IN]: [(props.selectedFolder as AssetData)?.space_uid],
        },
      } : {}),
    };

    removeAllConditions(fieldsToRetain, defaultConditions);
    requestAnimationFrame(() => {
      adjustMarginBasedOnFilterHeight();
    });

    // dam-ui parity: clear all AI content on clear-all, retain only space_uids for context
    if (ai_suggestions) {
      setAiSuggestions({ space_uids: linkedSpaces });
    }
  };

  const adjustMarginBasedOnFilterHeight = () => {
    setTimeout(() => {
      const element = document.querySelector('.SpaceAssetsModalBody__items') as HTMLElement;
      const filterContent = document.querySelector('.SpaceAssetsModalFilter__content') as HTMLElement;

      const tableElement = document.querySelector('.SpaceAssetsModalBody__items .Table') as HTMLElement;
      const tableBodyElement = document.querySelector('.SpaceAssetsModalBody__items .Table .Table__body') as HTMLElement;

      const singleSelectTableElement = document.querySelector('.SpaceAssetsModalBody__items .single-select-wrapper .Table') as HTMLElement;
      const singleSelectTableBodyElement = document.querySelector('.SpaceAssetsModalBody__items .single-select-wrapper .Table .Table__body') as HTMLElement;

      const altTableElement = document.querySelector('.list-view-wrapper .Table') as HTMLElement;
      const altTableBodyElement = document.querySelector('.Table .Table__body') as HTMLElement;

      if (element && filterContent) {
        const height = filterContent.offsetHeight;

        const isAssetPickerMode =
          !document.querySelector('.SpaceAssetsModal--upload-target') &&
          !document.querySelector('.SpaceAssetsModal--move-target');

        const targetTable = tableElement || singleSelectTableElement || altTableElement;
        const targetTableBody = tableBodyElement || singleSelectTableBodyElement || altTableBodyElement;

        element.style.removeProperty('margin-top');
        if (targetTable) targetTable.style.removeProperty('height');
        if (targetTableBody) targetTableBody.style.removeProperty('height');

        if (singleSelectTableElement && singleSelectTableElement !== targetTable) {
          singleSelectTableElement.style.removeProperty('height');
        }
        if (singleSelectTableBodyElement && singleSelectTableBodyElement !== targetTableBody) {
          singleSelectTableBodyElement.style.removeProperty('height');
        }

        let marginTop = '0rem';
        let tableHeight = 'calc(80vh - 21.3rem)';
        const bannerExists = !!document.querySelector('.SpaceAssetsModalBody__locale-info-banner');

        if(bannerExists) {
            marginTop = (viewType === SPACE_ASSETS_VIEW_TYPE.LIST || viewType === SPACE_ASSETS_VIEW_TYPE.GALLERY) ? '3rem' : '2rem';

            if(filterContent.offsetHeight > 140) {
              tableHeight = 'calc(80vh - 24.3rem)'
            }
        }
        else {
            if(filterContent.offsetHeight > 70 && filterContent.offsetHeight <= 140) {
                marginTop = '2rem';
            }
            else if(filterContent.offsetHeight > 140){
                marginTop = '4rem';
            }
        }

        element.style.setProperty('margin-top', marginTop, 'important');

        if (targetTable) {
          targetTable.style.setProperty('height', tableHeight, 'important');
        }

        if (targetTableBody) {
          targetTableBody.style.setProperty('height', tableHeight, 'important');
        }

        if (singleSelectTableElement && singleSelectTableElement !== targetTable) {
          singleSelectTableElement.style.setProperty('height', tableHeight, 'important');
        }

        if (singleSelectTableBodyElement && singleSelectTableBodyElement !== targetTableBody) {
          singleSelectTableBodyElement.style.setProperty('height', tableHeight, 'important');
        }
      } else {
        console.error('Required elements not found:', {
          element: !!element,
          filterContent: !!filterContent
        });
      }

      window.dispatchEvent(new CustomEvent('adjustBannerPosition'));
    }, 0);
  };

  useEffect(() => {
    adjustMarginBasedOnFilterHeight();
  }, [viewType, props.localeInfoBannerVisible]);

  useEffect(() => {
    const filterContent = document.querySelector('.SpaceAssetsModalFilter__content') as HTMLElement;
    if (!filterContent) return;

    const observer = new ResizeObserver(() => {
      adjustMarginBasedOnFilterHeight();
    });

    observer.observe(filterContent);

    return () => observer.disconnect();
  }, []);


  const moreFilterActionList = useMemo(() => {
    return [
      {
        label: 'View All Filters',
        id: 'view-all-filters',
        value: 'view-all-filters',
        action: () => {
          setShowViewAllFilters(true);
          setDropDownContent(() => (closeDropdown) => (
            getFieldsListComp(closeDropdown)
          ));
        }
      },
      {
        label: (
          <>
            {'Clear All'} (<b>{appliedFilterCount || 0}</b>) {'Filters'}
          </>
        ),
        id: 'clear-all-filters',
        value: 'clear-all-filters',
        action: () => clearAllFilters()
      }
    ];
  }, [filters]);

  const componentResponsiveness: IQueryPillsComponentResponsiveness = useMemo(() => {
    return {
      isResponsive: props.isResponsive,
      screen: props.screenVariant,
      moreFilterAction: {
        pillsRenderContainerWidth: ['sm', 'md'].includes(props.screenVariant) ? 0.3 : 0.5,
        viewAllFilterState: {
          showViewAllFilterDropdown: showViewAllFilters,
          openOrCloseViewAllDropdown: setShowViewAllFilters,
          viewAllFilters: dropDownContent
        },
        actionList: moreFilterActionList,
        defaultSelected: {
          id: '',
          label: ''
        }
      }
    };
  }, [props.isResponsive, showViewAllFilters, filters, props.screenVariant]);

  return (
    <div className="SpaceAssetsModalFilter">
      <div className="SpaceAssetsModalFilter__content">
        <QueryPills
          filterPillsArr={filters}
          leadingIcon="Filter"
          hidePillsTrailingIcon={true}
          dropDownContent={dropDownContent}
          onPillsClick={(pill: any) => {
            renderDropdownContent(pill);
          }}
          onHideFilterClick={() => {
            adjustMarginBasedOnFilterHeight();
          }}
          onClearAllFilters={clearAllFilters}
          filterCount={appliedFilterCount}
          truncateOptions={{
            truncateFrom: 'middle',
            maxChar: 20,
            isResponsive: false,
            tooltip: {
              position: 'right',
              showArrow: true,
              variantType: 'dark',
            },
            delimiter: '/',
          } as TruncateProps}
          componentResponsiveness={componentResponsiveness}
          isDisabled={props.shouldDisabled}>
        </QueryPills>
      </div>
    </div>
  );
};

export default React.memo(SpaceAssetsModalFilter);
